// © 2026 AttainIP Engineering
/**
 * Tag Loader — consent-aware dynamic script loading
 *
 * Loads third-party advertising/analytics scripts only after
 * the user has granted the relevant consent category.
 *
 * CRITICAL: The gtag stub uses the `arguments` keyword instead of
 * ES6 rest parameters. This is required because gtag.js uses
 * Array.isArray() to distinguish commands from event objects.
 * Using rest params would create a real Array, causing commands
 * to be silently skipped during dataLayer replay.
 */

import { hasAdvertisementConsent, hasAnalyticsConsent, isAnalyticsNotDeclined } from './consent';
import { initMixpanel } from './mixpanel';
import { GA4_MEASUREMENT_ID, GOOGLE_ADS_ID, LINKEDIN_PARTNER_ID } from '../config/analytics';

/** The LinkedIn Insight stub: a callable that buffers into its own `.q`
 *  until insight.min.js replaces it. `q` is optional because the stub is
 *  installed one statement before the queue is attached. */
type Lintrk = ((a: string, b?: unknown) => void) & { q?: Array<[string, unknown]> };

/** Third-party globals installed by the loaders below. All optional — none
 *  exists until the matching consent category is granted. */
type TagWindow = Window & {
  dataLayer?: IArguments[];
  gtag?: (...args: unknown[]) => void;
  lintrk?: Lintrk;
  _linkedin_partner_id?: string;
  _linkedin_data_partner_ids?: string[];
};

let ga4Loaded = false;
let googleAdsConfigured = false;
let linkedInTagLoaded = false;

/**
 * Dynamically load Google Analytics 4 (GA4) and initialize the gtag function.
 * No-op if analytics consent has not been granted or if already loaded.
 *
 * IMPORTANT: GA4 must be the primary container. The script loads with the
 * GA4 measurement ID, NOT the Google Ads ID. Google Ads configs are added
 * separately via loadGoogleTag().
 *
 * Uses isAnalyticsNotDeclined() to match marketing site opt-out model.
 */
export function loadGA4Tag(): void {
  if (ga4Loaded) return;
  if (!isAnalyticsNotDeclined()) return;

  // 1. Initialize the dataLayer array
  (window as TagWindow).dataLayer = (window as TagWindow).dataLayer || [];

  // 2. Define the gtag stub function using `arguments` keyword
  // CRITICAL: Do NOT use rest parameters (...args) here!
  function gtag(): void {
    // eslint-disable-next-line prefer-rest-params
    (window as TagWindow).dataLayer!.push(arguments);
  }
  (window as TagWindow).gtag = gtag;

  // 3. Append the gtag.js script with GA4 as the primary container
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // 4. Initialize GA4 config
  (window as TagWindow).gtag!('js', new Date());
  (window as TagWindow).gtag!('config', GA4_MEASUREMENT_ID);

  ga4Loaded = true;
}

/**
 * Add Google Ads configuration to the existing gtag instance.
 * No-op if advertisement consent has not been granted or if already configured.
 *
 * IMPORTANT: This does NOT load a separate script. It piggybacks on the
 * GA4 gtag script loaded by loadGA4Tag(). Only call this after GA4 is loaded.
 */
export function loadGoogleTag(): void {
  if (googleAdsConfigured) return;
  if (!hasAdvertisementConsent()) return;
  if (!ga4Loaded) return; // Can't configure Ads without GA4 script

  if ((window as TagWindow).gtag) {
    (window as TagWindow).gtag!('config', GOOGLE_ADS_ID);
    googleAdsConfigured = true;
  }
}

/**
 * Dynamically load the LinkedIn Insight Tag.
 * No-op if advertisement consent has not been granted or if already loaded.
 */
export function loadLinkedInTag(): void {
  if (linkedInTagLoaded) return;
  if (!hasAdvertisementConsent()) return;

  // Set partner ID globals
  (window as TagWindow)._linkedin_partner_id = LINKEDIN_PARTNER_ID;
  (window as TagWindow)._linkedin_data_partner_ids =
    (window as TagWindow)._linkedin_data_partner_ids || [];
  (window as TagWindow)._linkedin_data_partner_ids!.push(LINKEDIN_PARTNER_ID);

  // Create the lintrk queue if it doesn't exist
  if (!(window as TagWindow).lintrk) {
    (window as TagWindow).lintrk = function (a: string, b?: unknown) {
      (window as TagWindow).lintrk!.q!.push([a, b]);
    };
    (window as TagWindow).lintrk!.q = [];
  }

  // Append the insight.min.js script
  const s = document.getElementsByTagName('script')[0];
  const b = document.createElement('script');
  b.type = 'text/javascript';
  b.async = true;
  b.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
  s.parentNode?.insertBefore(b, s);

  linkedInTagLoaded = true;
}

/**
 * Master initialiser — call on page load and after consent changes.
 * Loads every tag whose consent category is currently granted.
 *
 * Order matters:
 * 1. GA4 loads first (analytics consent) - primary container
 * 2. Google Ads configs on top of GA4 (advertisement consent)
 * 3. LinkedIn loads independently (advertisement consent)
 * 4. Mixpanel loads (analytics consent)
 */
export function initConsentAwareTags(): void {
  // Load GA4 first if analytics consent granted (opt-out model)
  loadGA4Tag();

  // Add Google Ads config if advertisement consent granted
  loadGoogleTag();

  // Load LinkedIn tag if advertisement consent granted
  loadLinkedInTag();

  // Initialize Mixpanel if analytics consent granted
  if (hasAnalyticsConsent()) {
    initMixpanel();
  }
}
